import { useEffect, useState, useCallback } from "react";
import {
  Box, Typography, Paper, Table, TableHead, TableRow,
  TableCell, TableBody, Button, TextField, IconButton,
  InputAdornment, Chip, Tooltip, Skeleton, Pagination,
  Select, MenuItem, FormControl, InputLabel, Collapse,
  Divider, Badge
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PrintIcon from "@mui/icons-material/Print";

import { soldBikeService } from "../service/SoldBikeService";
import { invoiceService } from "../service/InvoiceService";
import InvoicePopup from "./InvoicePopup";
import { buildInvoiceHTML } from "./Invoicetemplate";

/* ─── helpers ─────────────────────────────────── */
const EMPTY_FILTERS = {
  model: "", location: "", customerName: "", mobile: "",
  saleType: "", paymentStatus: "", engineNumber: "",
  chasisNumber: "", 
  fromDate: "", toDate: "", month: "", year: "", search: "",
};

const cleanParams = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== ""));

const activeFilterCount = (f) =>
  Object.entries(f).filter(([k, v]) => v !== "" && k !== "search").length;

/* ─── component ───────────────────────────────── */
const InvoiceList = () => {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(false);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1, limit: 10 });

  const [filters, setFilters]     = useState(EMPTY_FILTERS);
  const [applied, setApplied]     = useState({});
  const [showFilters, setShowFilters] = useState(true);

  const [open, setOpen]           = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(null); // saleId being loaded
  const [bulkPrinting, setBulkPrinting] = useState(false);

  /* ── fetch ── */
  const fetchData = useCallback(async (params = {}, page = 1) => {
    setLoading(true);
    try {
      const res = await soldBikeService.getFilteredSoldBikes({
        ...cleanParams(params),
        page,
        limit: pagination.limit,
      });
      setData(res.data ?? []);
      setPagination((p) => ({ ...p, ...res.pagination, page }));
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit]);

  useEffect(() => { fetchData(); }, []);

  /* ── filter handlers ── */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((f) => ({ ...f, [name]: value }));
  };

  const handleApply = () => {
    setApplied(filters);
    fetchData(filters, 1);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setApplied({});
    fetchData({}, 1);
  };

  const handlePageChange = (_, page) => {
    fetchData(applied, page);
  };

  /* ── invoice (single) ── */
  const handleInvoice = async (saleId) => {
    setInvoiceLoading(saleId);
    try {
      const res = await invoiceService.getInvoice(saleId);
      setInvoiceData(res);
      setOpen(true);
    } catch (err) {
      console.error("Invoice error:", err);
    } finally {
      setInvoiceLoading(null);
    }
  };

  /* ── invoice (bulk print — all rows on the current page, same format) ── */
  const handlePrintAll = async () => {
    if (!data.length) return;
    setBulkPrinting(true);
    try {
      const saleIds = data.map((b) => b.saleId ?? b._id);
      const invoices = await Promise.all(
        saleIds.map((id) => invoiceService.getInvoice(id))
      );

      const pages = invoices
        .map(
          (inv, idx) => `
            <div style="${idx < invoices.length - 1 ? "page-break-after: always;" : ""} page-break-inside: avoid; break-inside: avoid;">
              ${buildInvoiceHTML(inv, true)}
            </div>
          `
        )
        .join("");

      const win = window.open("", "", "width=1100,height=900");
      win.document.write(`
        <html>
          <head>
            <title>Tax Invoices — All (${invoices.length})</title>
            <style>
              @page { size: A4; margin: 8mm 10mm; }
              html, body { margin: 0; padding: 0; background: #fff; width: 100%; }
              *     { box-sizing: border-box; }
              img   { max-width: 100%; }
              table { page-break-inside: avoid; break-inside: avoid; }
            </style>
          </head>
          <body>
            ${pages}
            <scr` + `ipt>
              window.onload = function() {
                window.print();
                window.onafterprint = function() { window.close(); };
              };
            </scr` + `ipt>
          </body>
        </html>
      `);
      win.document.close();
    } catch (err) {
      console.error("Bulk print error:", err);
    } finally {
      setBulkPrinting(false);
    }
  };

  const activeCount = activeFilterCount(applied);

  /* ── render ── */
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 2,
            bgcolor: "primary.main", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <TwoWheelerIcon sx={{ color: "#fff", fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>Invoice</Typography>
            <Typography variant="caption" color="text.secondary">
              {pagination.total} total records · Showroom-wise view
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={data.length ? "Print all invoices on this page" : "No records to print"}>
            <span>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={handlePrintAll}
                disabled={!data.length || bulkPrinting}
                size="small"
              >
                {bulkPrinting ? "Preparing…" : "Print All"}
              </Button>
            </span>
          </Tooltip>

          <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
            <Badge badgeContent={activeCount} color="primary" invisible={activeCount === 0}>
              <Button
                variant={showFilters ? "contained" : "outlined"}
                startIcon={<FilterListIcon />}
                onClick={() => setShowFilters((s) => !s)}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </Badge>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filter Panel ── */}
      <Collapse in={showFilters}>
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2.5, borderRadius: 2 }}>
          {/* Row 1 */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
            <TextField size="small" label="Model" name="model"
              value={filters.model} onChange={handleChange} sx={{ minWidth: 140, flex: 1 }} />

            <TextField size="small" label="Location" name="location"
              value={filters.location} onChange={handleChange} sx={{ minWidth: 130, flex: 1 }} />

            <TextField size="small" label="Customer" name="customerName"
              value={filters.customerName} onChange={handleChange} sx={{ minWidth: 150, flex: 1 }} />

            <TextField size="small" label="Mobile" name="mobile"
              value={filters.mobile} onChange={handleChange} sx={{ minWidth: 130, flex: 1 }} />

            <FormControl size="small" sx={{ minWidth: 120, flex: 1 }}>
              <InputLabel>Sale Type</InputLabel>
              <Select name="saleType" value={filters.saleType} label="Sale Type" onChange={handleChange}>
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="CASH">Cash</MenuItem>
                <MenuItem value="FINANCE">Finance</MenuItem>
                <MenuItem value="EXCHANGE">Exchange</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 130, flex: 1 }}>
              <InputLabel>Due Status</InputLabel>
              <Select name="paymentStatus" value={filters.paymentStatus} label="Due Status" onChange={handleChange}>
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value="PAID">Paid</MenuItem>
                <MenuItem value="DUE">Due</MenuItem>
              </Select>
            </FormControl>

            <TextField size="small" label="Engine No" name="engineNumber"
              value={filters.engineNumber} onChange={handleChange} sx={{ minWidth: 150, flex: 1 }} />
          </Box>

          {/* Row 2 */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
            <TextField size="small" label="Chasis No" name="chasisNumber"
              value={filters.chasisNumber} onChange={handleChange} sx={{ minWidth: 150, flex: 1 }} />

         

            <TextField size="small" label="From Date" name="fromDate" type="date"
              value={filters.fromDate} onChange={handleChange}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, flex: 1 }} />

            <TextField size="small" label="To Date" name="toDate" type="date"
              value={filters.toDate} onChange={handleChange}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 150, flex: 1 }} />

            <TextField size="small" label="Month" name="month" type="number"
              inputProps={{ min: 1, max: 12 }}
              value={filters.month} onChange={handleChange} sx={{ minWidth: 90, flex: "0 0 90px" }} />

            <TextField size="small" label="Year" name="year" type="number"
              value={filters.year} onChange={handleChange} sx={{ minWidth: 100, flex: "0 0 100px" }} />
          </Box>

          {/* Row 3: Search + Buttons */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
            <TextField
              size="small" label="Search" name="search"
              value={filters.search} onChange={handleChange}
              placeholder="Engine / Chassis / Customer / Mobile…"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 260, flex: 2 }}
            />

            <Button variant="contained" onClick={handleApply} sx={{ px: 3, height: 40 }}>
              Apply Filter
            </Button>
            <Button
              variant="outlined" startIcon={<RestartAltIcon />}
              onClick={handleReset} sx={{ height: 40 }}
            >
              Reset
            </Button>

            {activeCount > 0 && (
              <Chip
                size="small"
                label={`${activeCount} filter${activeCount > 1 ? "s" : ""} active`}
                color="primary" variant="outlined"
                onDelete={handleReset}
              />
            )}
          </Box>
        </Paper>
      </Collapse>

      {/* ── Table ── */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.50" }}>
              {["#", "Model", "Engine No", "Chassis No", "Customer", "Mobile", "Location" ,"ExShowroom Price", "Sale Date", "Invoice"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, whiteSpace: "nowrap", py: 1.5 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 11 }).map((__, j) => (
                    <TableCell key={j}><Skeleton variant="text" width="80%" /></TableCell>
                  ))}
                </TableRow>
              ))
              : data.length === 0
                ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ py: 6, color: "text.secondary" }}>
                      <ReceiptLongIcon sx={{ fontSize: 40, mb: 1, opacity: 0.3, display: "block", mx: "auto" }} />
                      No records found
                    </TableCell>
                  </TableRow>
                )
                : data.map((b, idx) => (
                  <TableRow key={b._id} hover>
                    <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                      {(pagination.page - 1) * pagination.limit + idx + 1}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{b.model || "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{b.engineNumber || "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>{b.chasisNumber || "—"}</TableCell>
                    <TableCell>{b.customerName || "—"}</TableCell>
                    <TableCell>{b.mobile?.[0] || "—"}</TableCell>
                    <TableCell>{b?.location}</TableCell>
                 
                  
                    <TableCell sx={{ fontWeight: 600 }}>
                      {b.salePrice ? `₹ ${Number(b.salePrice).toLocaleString("en-IN")}` : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, whiteSpace: "nowrap" }}>
                      {b.saleDate ? new Date(b.saleDate).toLocaleDateString("en-IN") : "—"}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained" size="small"
                        startIcon={<ReceiptLongIcon fontSize="small" />}
                        loading={invoiceLoading === (b.saleId ?? b._id)}
                        onClick={() => handleInvoice(b.saleId ?? b._id)}
                        sx={{ fontSize: 12, px: 1.5, whiteSpace: "nowrap" }}
                      >
                        Invoice
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
            }
          </TableBody>
        </Table>

        {/* ── Pagination ── */}
        {pagination.totalPages > 1 && (
          <>
            <Divider />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2, py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                Showing {(pagination.page - 1) * pagination.limit + 1}–
                {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
              </Typography>
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                onChange={handlePageChange}
                size="small" color="primary"
              />
            </Box>
          </>
        )}
      </Paper>

      {/* ── Invoice Popup ── */}
      <InvoicePopup
        open={open}
        onClose={() => { setOpen(false); setInvoiceData(null); }}
        data={invoiceData}
      />
    </Box>
  );
};

export default InvoiceList;